import { useState, useEffect } from "react";
import { Modal, Popover } from "antd";
import AddTargets from "./AddTargets";
import ReviewBulkKeywords from "./ReviewBulkKeywords";
import CardsLayout from "./CardsLayout";
import {
    appendKeywordsInformation,
    actionBtnClickHandler,
    getCampaignsOfPortfolios,
    confirmCampaignsHandler,
    reviewSelectionHandler,
    bulkKeywordsClickHandler,
    backButtonClickHandler,
    resetDynamicOptions,
} from "@ts/bulk-keywords-addition";
import { createPortfoliosOptions, setSpinner } from "@ts/utils";
import BulkActionDropDown from "./BulkActionDropDown";
import { TargetTypeOption } from "@ts/multi-select-dropdown";
import $ from "jquery";
import { useDispatch, useSelector } from "react-redux";
import { updateKeywordsSelectionData } from "@features/selectedDataSlice";
import { GridApi, GridReadyEvent } from "ag-grid-community";
import {
    Channels,
    BULK_TGT_ACTION,
    IChannel,
    Keyword,
    TBulkActionsFooterBtnValues,
    TCommonRowData,
    TCommonRowObj,
    TKeywordsActionObj,
    TKeywordsSelectionData,
    ProgramType,
    MatchType,
    ActionLevel,
} from "@ts/common-types";
import { Dispatch } from "redux";
import { FaInfoCircle } from "react-icons/fa";
import { useGetModulesData } from "@/hooks/AllCustomHooks";

interface ICampaignProps {
    show: boolean;
    setShow: (show: boolean) => void;
    gridApi: GridApi;
}

const BulkActionModal: React.FC<ICampaignProps> = ({ show, setShow }) => {
    const [charLimit, setCharLimit] = useState(50);
    const [fullLength, setFullLength] = useState<number>();
    const [removeTargetsDisplay, setRemoveTargetsDisplay] =
        useState<string>("none");
    const [selectedPortfolios, setSelectedPortfolios] = useState([]);
    const [selectedProgramType, setSelectedProgramType] = useState([]);
    const [selectedCampaignType, setSelectedCampaignType] = useState([]);
    const [selectedClientCampaigns, setSelectedClientCampaigns] = useState([]);
    const [selectedAdgroups, setSelectedAdgroups] = useState([]);
    const [selectedMatchTypes, setSelectedMatchTypes] = useState([]);
    const [matchType, setMatchType] = useState<TargetTypeOption[]>([]);
    const [portfoliosOptions, setPortfoliosOptions] = useState<
        TargetTypeOption[]
    >([]);
    const [bulkKeywordColumnDefs, setBulkKeywordColumnDefs] = useState([]);
    const [bulkKeywordsRowData, setBulkKeywordsRowData] = useState<
        TKeywordsActionObj[]
    >([]);
    const [bulkKeywordsGridApi, setBulkKeywordsGridApi] = useState<GridApi>();
    const fullLengthStorage = useSelector(
        (state: TKeywordsSelectionData) => state.keywordsSelectionData.value
    );
    const channel = useSelector((state: IChannel) => state.channel.value);
    const dispatch = useDispatch<Dispatch>();

    const [inputData, setInputData] = useState({
        action: "",
        actionlevel: "",
    });
    const [clientCampaignOptions, setClientCampaignOptions] = useState([]);
    const [adGroupOptions, setAdGroupOptions] = useState([]);
    const [isShowMatchType, setIsShowMatchType] = useState(false);
    const [footerBtnValues, setFooterBtnValues] =
        useState<TBulkActionsFooterBtnValues>({
            isReviewSelectionBtnVisible: true,
            isReviewSelectionBtnDisabled: true,
            isAddKeywordBtnVisible: false,
            isAddKeywordBtnDisabled: true,
            isAddNegativeKeywordBtnVisible: false,
            isAddNegativeKeywordBtnDisabled: true,
            isBackBtnVisible: false,
        });
    const [kaModalStepCount, setKaModalStepCount] = useState(1);

    const {
        matchTypeOptions,
        programTypeOptions,
        kaCampaignTypeOptions,
        negativeMatchTypeOptions,
    } = useGetModulesData({ type: "dropdown-options" });

    const gridParams = useGetModulesData({ type: "grid-params" });

    useEffect(() => {
        setMatchType(
            inputData.action === BULK_TGT_ACTION.ADD_AS_KEYWORD
                ? matchTypeOptions
                : negativeMatchTypeOptions
        );
    }, [matchTypeOptions, negativeMatchTypeOptions, inputData.action]);

    const hideComponentOnReset = (cardType: string) => {
        switch (cardType) {
            case "program-type":
                setIsShowMatchType(false);
                setClientCampaignOptions([]);
                setAdGroupOptions([]);
                break;
            case "campaigns":
                setIsShowMatchType(false);
                setAdGroupOptions([]);
                break;
        }
    };

    const resetAll = () => {
        createPortfoliosOptions(setPortfoliosOptions);
        setInputData({
            action: "",
            actionlevel: "",
        });
        setIsShowMatchType(false);
        setClientCampaignOptions([]);
        resetToInitialState();
    };

    const resetOnPortfolios = () => {
        if (selectedProgramType.length > 0) {
            setSelectedProgramType([]);
            resetOnProgramTypeChange();
        }
    };

    const resetOnProgramTypeChange = () => {
        if (selectedCampaignType.length > 0) {
            setSelectedCampaignType([]);
            resetOnCampaignTypeChange();
            hideComponentOnReset("program-type");
        }
    };

    const resetOnCampaignTypeChange = () => {
        if (selectedClientCampaigns.length > 0) {
            setSelectedClientCampaigns([]);
            resetOnCampaignsChange();
            resetDynamicOptions();
        }
        window.sessionStorage.removeItem("campaignList");
        hideComponentOnReset("program-type");
    };

    const resetOnCampaignsChange = () => {
        if (selectedCampaignType.length > 0) {
            setSelectedAdgroups([]);
            setSelectedMatchTypes([]);
            setBulkKeywordColumnDefs([]);
            setBulkKeywordsRowData([]);
        }
        hideComponentOnReset("campaigns");
        setFooterBtnValues((prev) => ({
            ...prev,
            isReviewSelectionBtnDisabled: true,
        }));
    };

    const resetOnAdgroupsChange = () => {
        if (selectedAdgroups.length > 0) {
            setSelectedMatchTypes([]);
            setBulkKeywordColumnDefs([]);
            setBulkKeywordsRowData([]);
        }
        setIsShowMatchType(false);
        setFooterBtnValues((prev) => ({
            ...prev,
            isReviewSelectionBtnDisabled: true,
        }));
    };

    const resetOnButtonClick = (cardType: string) => {
        if (cardType === "campaigns-card") {
            setSelectedClientCampaigns([]);
        }
        resetOnCampaignsChange();
    };

    const getSelectedDataLength = (selectedData: TCommonRowData) => {
        let selectedDataLength = 0;
        if (selectedData.length) {
            const checkSelectAll = selectedData.filter(
                (obj: TCommonRowObj) => obj.value === "*"
            );
            selectedDataLength = checkSelectAll.length
                ? selectedData.length - 1
                : selectedData.length;
        }

        return selectedDataLength;
    };

    function onBulkKeywordSelectionChanged(event: GridReadyEvent) {
        const selectedNodes = event.api.getSelectedNodes();
        const actionType = inputData.action;
        $("#total-selected-combinations-count")[0].textContent =
            selectedNodes.length.toString();

        if (selectedNodes.length) {
            switch (actionType) {
                case BULK_TGT_ACTION.ADD_AS_NEG_KEYWORD:
                    setFooterBtnValues((prev) => ({
                        ...prev,
                        isAddNegativeKeywordBtnDisabled: false,
                        isAddNegativeKeywordBtnVisible: true,
                    }));
                    break;
                case BULK_TGT_ACTION.ADD_AS_KEYWORD:
                    setFooterBtnValues((prev) => ({
                        ...prev,
                        isAddKeywordBtnDisabled: false,
                        isAddKeywordBtnVisible: true,
                    }));
                    break;
                default:
                    console.error("Select a valid action");
            }
        } else {
            setFooterBtnValues((prev) => ({
                ...prev,
                isAddNegativeKeywordBtnDisabled: true,
                isAddNegativeKeywordBtnVisible: false,
                isAddKeywordBtnDisabled: true,
                isAddKeywordBtnVisible: false,
            }));
        }
    }

    useEffect(() => {
        if (show) {
            setFooterBtnValues((prev) => ({
                ...prev,
                isReviewSelectionBtnDisabled: true,
            }));
            if (fullLengthStorage.length) {
                appendKeywordsInformation(charLimit);
                setFullLength(fullLengthStorage[0].keyword_name.length);
            }
        } else {
            resetToInitialState();
            sessionStorage.removeItem("selectedData");
        }
    }, [charLimit, show, fullLengthStorage]);

    const resetToInitialState = () => {
        setSelectedPortfolios([]);
        setSelectedProgramType([]);
        setSelectedCampaignType([]);
        setSelectedClientCampaigns([]);
        setSelectedAdgroups([]);
        setSelectedMatchTypes([]);
        setBulkKeywordColumnDefs([]);
        setBulkKeywordsRowData([]);
        setClientCampaignOptions([]);
        setAdGroupOptions([]);
        resetDynamicOptions();
        setFooterBtnValues((prev) => ({
            ...prev,
            isReviewSelectionBtnDisabled: true,
        }));
        window.sessionStorage.removeItem("unsavedSelectedData");
        window.sessionStorage.removeItem("campaignList");
    };

    const onActionLevelChangeHandler = (event: any) => {
        const actionLevel =
            event.target.id === "campaigns"
                ? ActionLevel.Campaign
                : ActionLevel.AdGroup;
        setInputData((prev) => ({
            ...prev,
            actionlevel: actionLevel,
        }));
        setIsShowMatchType(false);
        resetOnProgramTypeChange();
        setSelectedProgramType([]);
        setSelectedPortfolios([]);
    };

    const popoverHoverFocus = (
        <CardsLayout
            heading={<strong>Negative keywords match-type constraints</strong>}
            cardId="popover-card"
            display="unset"
        >
            <p>
                Negative-phrase: Maximum word count is 4 and maximum character
                count is 80.
                <br />
                Negative-exact: Maximum word count is 10 and maximum character
                count is 80.
                <br />
            </p>
        </CardsLayout>
    );

    const handleClose = () => {
        dispatch(
            updateKeywordsSelectionData(
                fullLengthStorage.filter((node: Keyword) => !node.custom)
            )
        );
        resetToInitialState();
        setRemoveTargetsDisplay("none");
        setCharLimit(50);
        setShow(!show);
    };

    const targetsHandler = (event: any) => {
        createPortfoliosOptions(setPortfoliosOptions);
        actionBtnClickHandler(event, resetToInitialState, setIsShowMatchType);
        setInputData((prev) => ({
            ...prev,
            action:
                event.target.id === "add-keyword-btn"
                    ? BULK_TGT_ACTION.ADD_AS_KEYWORD
                    : BULK_TGT_ACTION.ADD_AS_NEG_KEYWORD,
            actionlevel:
                event.target.id === "add-keyword-btn"
                    ? ActionLevel.AdGroup
                    : "",
        }));
    };

    const ModalHeaderElement = () => (
        <div
            className="d-flex align-items-center pb-3"
            style={{ fontSize: "16px" }}
        >
            <div>Bulk Target Action</div>
            <div className="ms-1">
                <select
                    className="selected-filter badge border bg-light fw-normal text-dark ms-1 mt-1 position-relative d-inline"
                    id="keywordnamelength"
                    name="keywordNameLength"
                    defaultValue={charLimit}
                    onChange={(e) => setCharLimit(parseInt(e.target.value))}
                >
                    <option value={fullLength}>Full name</option>
                    <option value={25}>25 characters</option>
                    <option value={50}>50 characters</option>
                    <option value={75}>75 characters</option>
                </select>
            </div>

            <div className="ms-2">
                <Popover
                    placement="bottom"
                    content={popoverHoverFocus}
                    styles={{ body: { padding: 0, width: "276px" } }}
                >
                    <FaInfoCircle />
                </Popover>
            </div>
        </div>
    );

    return (
        <Modal
            open={show}
            title={<ModalHeaderElement />}
            onOk={handleClose}
            onCancel={handleClose}
            footer={[
                <button
                    className="btn btn-warning me-2"
                    id="ka-review-grid-back"
                    style={{
                        display: footerBtnValues.isBackBtnVisible
                            ? "unset"
                            : "none",
                        color: "white",
                    }}
                    onClick={() => {
                        backButtonClickHandler(
                            setFooterBtnValues,
                            setKaModalStepCount
                        );
                    }}
                >
                    Back
                </button>,
                <button
                    id="review-selection-grid-btn"
                    className="btn btn-primary me-2"
                    onClick={() =>
                        reviewSelectionHandler(
                            selectedMatchTypes,
                            selectedProgramType,
                            selectedAdgroups,
                            bulkKeywordsGridApi,
                            setBulkKeywordColumnDefs,
                            setBulkKeywordsRowData,
                            setFooterBtnValues,
                            setKaModalStepCount,
                            inputData.actionlevel,
                            gridParams.isAdgroupSelectionRequired,
                            gridParams.isBidRequired,
                            gridParams.isMatchTypeReviewRequired,
                            gridParams.isMatchTypeSelectionRequired,
                            gridParams.excludedBidProgramType || null
                        )
                    }
                    disabled={footerBtnValues.isReviewSelectionBtnDisabled}
                    style={{
                        display: footerBtnValues.isReviewSelectionBtnVisible
                            ? "unset"
                            : "none",
                    }}
                >
                    Review Selection
                </button>,

                <button
                    className="btn btn-success me-2"
                    id="keyword-add-btn"
                    data-bs-name="Add"
                    style={{
                        display: footerBtnValues.isAddKeywordBtnVisible
                            ? "unset"
                            : "none",
                    }}
                    onClick={(
                        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                    ) => {
                        setSpinner("add", event, true);
                        bulkKeywordsClickHandler(
                            bulkKeywordsGridApi,
                            event,
                            gridParams.isAdgroupSelectionRequired,
                            gridParams.isBidRequired,
                            gridParams.isMatchTypeSelectionRequired
                        );
                    }}
                    disabled={footerBtnValues.isAddKeywordBtnDisabled}
                >
                    Add
                </button>,

                <button
                    className="btn btn-success me-2"
                    id="add-negative-btn"
                    data-bs-name="Add Negative"
                    style={{
                        display: footerBtnValues.isAddNegativeKeywordBtnVisible
                            ? "unset"
                            : "none",
                    }}
                    onClick={(
                        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                    ) => {
                        setSpinner("add", event, true);
                        bulkKeywordsClickHandler(
                            bulkKeywordsGridApi,
                            event,
                            gridParams.isAdgroupSelectionRequired,
                            gridParams.isBidRequired,
                            gridParams.isMatchTypeSelectionRequired
                        );
                    }}
                    disabled={footerBtnValues.isAddNegativeKeywordBtnDisabled}
                >
                    Add Negative
                </button>,

                <button
                    className="btn btn-secondary"
                    data-bs-dismiss="modal"
                    onClick={handleClose}
                >
                    Close
                </button>,
            ]}
            style={{ top: 20 }}
            width="95vw"
            styles={{
                body: {
                    height: "72vh",
                    overflowY: "auto",
                },
            }}
        >
            <div style={{ height: "100%" }}>
                <AddTargets
                    removeTargetsDisplay={removeTargetsDisplay}
                    setRemoveTargetsDisplay={setRemoveTargetsDisplay}
                    resetAll={resetAll}
                />

                <div
                    id="keyword-analytics-first-step"
                    style={{
                        display: kaModalStepCount === 1 ? "unset" : "none",
                    }}
                >
                    <form id="keyword-addition-form">
                        <div style={{ width: "97%", margin: "auto 1rem" }}>
                            <div className="row row-cols-2 g-3">
                                <CardsLayout
                                    heading="Select Action"
                                    cardId="action"
                                    display="unset"
                                >
                                    <button
                                        className={`btn w-auto me-3 ${
                                            inputData.action ===
                                            BULK_TGT_ACTION.ADD_AS_KEYWORD
                                                ? "btn-primary selected"
                                                : "btn-outline-secondary"
                                        }`}
                                        id="add-keyword-btn"
                                        type="button"
                                        onClick={targetsHandler}
                                    >
                                        Add Targets
                                    </button>
                                    <button
                                        className={`btn w-auto ${
                                            inputData.action ===
                                            BULK_TGT_ACTION.ADD_AS_NEG_KEYWORD
                                                ? "btn-danger selected"
                                                : "btn-outline-secondary"
                                        } ${
                                            gridParams.isNegativeAdditionAllowed
                                                ? "d-unset"
                                                : "d-none"
                                        }`}
                                        id="add-negative-keyword-btn"
                                        type="button"
                                        onClick={targetsHandler}
                                    >
                                        Add Negative Targets
                                    </button>
                                </CardsLayout>

                                {channel === Channels.AMAZON && (
                                    <>
                                        <CardsLayout
                                            heading="Selection action level"
                                            cardId="action-level"
                                            display={
                                                inputData.action
                                                    ? "unset"
                                                    : "none"
                                            }
                                        >
                                            <div className="form-check form-check-inline">
                                                <input
                                                    className="form-check-input"
                                                    id="campaigns"
                                                    type="radio"
                                                    name="action-level"
                                                    value="campaign"
                                                    onChange={
                                                        onActionLevelChangeHandler
                                                    }
                                                    checked={
                                                        inputData.actionlevel ===
                                                        "campaign"
                                                    }
                                                    disabled={
                                                        inputData.action ===
                                                        BULK_TGT_ACTION.ADD_AS_KEYWORD
                                                    }
                                                />
                                                <label
                                                    className="form-check-label"
                                                    htmlFor="campaigns"
                                                >
                                                    Campaigns
                                                </label>
                                            </div>
                                            <div className="form-check form-check-inline">
                                                <input
                                                    className="form-check-input"
                                                    id="adgroups"
                                                    type="radio"
                                                    name="action-level"
                                                    value="adgroup"
                                                    onChange={
                                                        onActionLevelChangeHandler
                                                    }
                                                    checked={
                                                        inputData.actionlevel ===
                                                        "adgroup"
                                                    }
                                                    disabled={
                                                        inputData.action ===
                                                        BULK_TGT_ACTION.ADD_AS_KEYWORD
                                                    }
                                                />
                                                <label
                                                    className="form-check-label"
                                                    htmlFor="adgroups"
                                                >
                                                    Adgroups
                                                </label>
                                            </div>
                                        </CardsLayout>

                                        <CardsLayout
                                            heading="Select Portfolio (optional)"
                                            cardId="ka-portfolio-dropdown"
                                            display={
                                                inputData.action ===
                                                BULK_TGT_ACTION.ADD_AS_KEYWORD
                                                    ? "unset"
                                                    : inputData.actionlevel
                                                    ? "unset"
                                                    : "none"
                                            }
                                        >
                                            <div>
                                                <BulkActionDropDown
                                                    id="portfolio-list"
                                                    options={portfoliosOptions}
                                                    setSelectedOptions={
                                                        setSelectedPortfolios
                                                    }
                                                    selectedOptions={
                                                        selectedPortfolios
                                                    }
                                                    placeholder="Nothing Selected"
                                                    onChangeInSelectedOptions={
                                                        resetOnPortfolios
                                                    }
                                                />
                                                <span className="text-success">
                                                    Total portfolio selected :{" "}
                                                    {getSelectedDataLength(
                                                        selectedPortfolios
                                                    )}
                                                </span>
                                            </div>
                                        </CardsLayout>
                                    </>
                                )}

                                <CardsLayout
                                    heading="Select Program Type"
                                    cardId="ka-program-type-dropdown"
                                    display={
                                        channel !== Channels.AMAZON ||
                                        inputData.action ===
                                            BULK_TGT_ACTION.ADD_AS_KEYWORD
                                            ? "unset"
                                            : inputData.actionlevel
                                            ? "unset"
                                            : "none"
                                    }
                                >
                                    <BulkActionDropDown
                                        id="program-type-list"
                                        options={programTypeOptions}
                                        setSelectedOptions={
                                            setSelectedProgramType
                                        }
                                        selectedOptions={selectedProgramType}
                                        placeholder="Nothing Selected"
                                        onChangeInSelectedOptions={
                                            resetOnProgramTypeChange
                                        }
                                    />
                                </CardsLayout>

                                <CardsLayout
                                    heading="Select Campaign Type"
                                    cardId="ka-campaign-type-dropdown"
                                    display={
                                        channel !== Channels.AMAZON ||
                                        inputData.action ===
                                            BULK_TGT_ACTION.ADD_AS_KEYWORD
                                            ? "unset"
                                            : inputData.actionlevel
                                            ? "unset"
                                            : "none"
                                    }
                                >
                                    <div className="d-flex">
                                        <BulkActionDropDown
                                            id="campaign-type-list"
                                            options={kaCampaignTypeOptions}
                                            setSelectedOptions={
                                                setSelectedCampaignType
                                            }
                                            selectedOptions={
                                                selectedCampaignType
                                            }
                                            placeholder="Nothing Selected"
                                            onChangeInSelectedOptions={
                                                resetOnCampaignTypeChange
                                            }
                                        ></BulkActionDropDown>
                                        <button
                                            className="btn btn-dark btn-sm ms-2 mt-1"
                                            type="button"
                                            id="confirm-campaign-filter"
                                            disabled={
                                                !selectedProgramType.length ||
                                                !selectedCampaignType.length
                                            }
                                            style={{
                                                height: "fit-content",
                                            }}
                                            onClick={() => {
                                                resetOnButtonClick(
                                                    "campaigns-card"
                                                );
                                                getCampaignsOfPortfolios(
                                                    selectedPortfolios,
                                                    selectedProgramType,
                                                    selectedCampaignType,
                                                    setClientCampaignOptions
                                                );
                                            }}
                                        >
                                            Get Campaigns
                                        </button>
                                    </div>
                                </CardsLayout>

                                <CardsLayout
                                    heading="Select Campaigns"
                                    cardId="get-campaigns"
                                    display={
                                        clientCampaignOptions.length > 1
                                            ? "unset"
                                            : "none"
                                    }
                                >
                                    <div>
                                        <div className="d-flex">
                                            <BulkActionDropDown
                                                id="campaign-list"
                                                options={clientCampaignOptions}
                                                setSelectedOptions={
                                                    setSelectedClientCampaigns
                                                }
                                                selectedOptions={
                                                    selectedClientCampaigns
                                                }
                                                placeholder="Nothing Selected"
                                                onChangeInSelectedOptions={
                                                    resetOnCampaignsChange
                                                }
                                                channel={channel}
                                                setFooterBtnValues={
                                                    setFooterBtnValues
                                                }
                                                setIsShowMatchType={
                                                    setIsShowMatchType
                                                }
                                                actionLevel={
                                                    inputData.actionlevel
                                                }
                                                isAdgroupSelectionRequired={
                                                    gridParams.isAdgroupSelectionRequired
                                                }
                                                isMatchTypeSelectionRequired={
                                                    gridParams.isMatchTypeSelectionRequired
                                                }
                                            ></BulkActionDropDown>

                                            <div
                                                style={{
                                                    display:
                                                        !gridParams.isAdgroupSelectionRequired
                                                            ? "none"
                                                            : inputData.action ===
                                                                  BULK_TGT_ACTION.ADD_AS_NEG_KEYWORD &&
                                                              inputData.actionlevel ===
                                                                  "campaign"
                                                            ? "none"
                                                            : "unset",
                                                }}
                                            >
                                                <button
                                                    className="btn btn-dark btn-sm ms-2 mt-1"
                                                    type="button"
                                                    id="confirm-campaigns"
                                                    disabled={
                                                        selectedClientCampaigns.length
                                                            ? false
                                                            : true
                                                    }
                                                    style={{
                                                        height: "fit-content",
                                                        color: "white",
                                                    }}
                                                    onClick={() => {
                                                        resetOnButtonClick(
                                                            "adgroups-card"
                                                        );
                                                        confirmCampaignsHandler(
                                                            selectedClientCampaigns,
                                                            setAdGroupOptions,
                                                            setFooterBtnValues,
                                                            setIsShowMatchType
                                                        );
                                                    }}
                                                >
                                                    Get Adgroups
                                                </button>
                                            </div>
                                        </div>
                                        <div className="mt-2 text-success">
                                            Total campaign selected :{" "}
                                            <span id="total-campaign-count">
                                                {getSelectedDataLength(
                                                    selectedClientCampaigns
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </CardsLayout>

                                <CardsLayout
                                    heading="Select Adgroups"
                                    cardId="get-adgroups"
                                    display={
                                        adGroupOptions.length ? "unset" : "none"
                                    }
                                >
                                    <BulkActionDropDown
                                        id="adgroup-list"
                                        options={adGroupOptions}
                                        setSelectedOptions={setSelectedAdgroups}
                                        selectedOptions={selectedAdgroups}
                                        placeholder="Nothing Selected"
                                        onChangeInSelectedOptions={
                                            resetOnAdgroupsChange
                                        }
                                        setIsShowMatchType={setIsShowMatchType}
                                        setFooterBtnValues={setFooterBtnValues}
                                    ></BulkActionDropDown>

                                    <div className="mt-2 text-success">
                                        Total adgroup selected :
                                        <span id="total-adgroup-count">
                                            {getSelectedDataLength(
                                                selectedAdgroups
                                            )}
                                        </span>
                                    </div>
                                </CardsLayout>

                                <CardsLayout
                                    heading="Select match type"
                                    cardId="match-type"
                                    display={
                                        gridParams.isMatchTypeSelectionRequired &&
                                        isShowMatchType
                                            ? "unset"
                                            : "none"
                                    }
                                >
                                    <BulkActionDropDown
                                        id="match-type-list"
                                        options={matchType}
                                        setSelectedOptions={
                                            setSelectedMatchTypes
                                        }
                                        selectedOptions={selectedMatchTypes}
                                        placeholder="Nothing Selected"
                                        setFooterBtnValues={setFooterBtnValues}
                                    ></BulkActionDropDown>
                                </CardsLayout>
                            </div>
                        </div>
                    </form>
                </div>
                <div id="keyword-analytics-second-step-loader">
                    <div className="spinner-loader-container">
                        <i className="spinner-border text-secondary" />
                        <div>Creating combinations...</div>
                    </div>
                </div>
                <ReviewBulkKeywords
                    bulkKeywordsRowData={bulkKeywordsRowData}
                    bulkKeywordsGridApi={bulkKeywordsGridApi}
                    setBulkKeywordsGridApi={setBulkKeywordsGridApi}
                    onBulkKeywordSelectionChanged={
                        onBulkKeywordSelectionChanged
                    }
                    bulkKeywordColumnDefs={bulkKeywordColumnDefs}
                    kaModalStepCount={kaModalStepCount}
                />
            </div>
        </Modal>
    );
};

export default BulkActionModal;
